/**
 * 拉普拉斯方差（Laplacian variance）清晰度：对灰度拉普拉斯响应 L 求方差 Var(L)。
 * 与「先平方再对平方求方差」不同，量级更小，阈值需单独标定（见 BLUR_THRESHOLD）。
 */
export const BLUR_THRESHOLD = 100;

/** 检测时缩放比例，减轻 canvas 计算量 */
export const CHECK_SCALE = 0.5;

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error("image load failed"));
		img.src = url;
	});
}

/**
 * 标准拉普拉斯方差清晰度分数：Var(L)，L 为 4-邻域离散拉普拉斯响应。
 */
export function checkImageSharpness(
	img: HTMLImageElement,
	options?: { checkScale?: number },
): number {
	const checkScale = options?.checkScale ?? CHECK_SCALE;
	const naturalWidth = img.naturalWidth || img.width;
	const naturalHeight = img.naturalHeight || img.height;
	if (!naturalWidth || !naturalHeight) {
		return 0;
	}

	const w = Math.max(1, Math.floor(naturalWidth * checkScale));
	const h = Math.max(1, Math.floor(naturalHeight * checkScale));
	// 至少需要 3×3 才有内部像素可卷积
	if (w < 3 || h < 3) {
		return 0;
	}

	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (!ctx) return 0;

	canvas.width = w;
	canvas.height = h;
	ctx.drawImage(img, 0, 0, w, h);

	const imageData = ctx.getImageData(0, 0, w, h);
	const data = imageData.data;

	const grayData: number[] = [];
	for (let i = 0; i < data.length; i += 4) {
		const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
		grayData.push(gray);
	}

	const laplacian: number[] = [];
	for (let i = 1; i < h - 1; i++) {
		for (let j = 1; j < w - 1; j++) {
			const idx = i * w + j;
			const val =
				4 * grayData[idx] -
				grayData[idx - w] -
				grayData[idx + w] -
				grayData[idx - 1] -
				grayData[idx + 1];
			laplacian.push(val);
		}
	}

	const n = laplacian.length;
	if (n === 0) {
		return 0;
	}

	const mean = laplacian.reduce((a, b) => a + b, 0) / n;
	const variance = laplacian.reduce((a, b) => a + (b - mean) ** 2, 0) / n;

	return Number.isFinite(variance) ? variance : 0;
}

export async function checkSharpnessFromBlob(
	file: Blob,
	options?: { blurThreshold?: number; checkScale?: number },
): Promise<{ score: number; ok: boolean }> {
	const blurThreshold = options?.blurThreshold ?? BLUR_THRESHOLD;
	const url = URL.createObjectURL(file);
	try {
		const img = await loadImageFromUrl(url);
		const score = checkImageSharpness(img, { checkScale: options?.checkScale });
		return { score, ok: score >= blurThreshold };
	} finally {
		URL.revokeObjectURL(url);
	}
}
